import {
	DatePickerProps,
	DateTimePickerProps,
	DatePicker as MuiDatePicker,
	DateTimePicker as MuiDateTimePicker
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { forwardRef } from 'react';
import { CircularProgress } from '../circular-progess';
import { IconButton } from '../icon-button';
import { useMuiClickToDismiss } from '../mui-click-to-dismiss';
import { ClickToDismissPortalContext } from '../popover';
import { ExtendedTextFieldProps, TextField } from '../text-field';

type BaseExtendedPickerProps = Pick<
	ExtendedTextFieldProps,
	'label' | 'status' | 'helperText' | 'size' | 'fullWidth' | 'onBlur' | 'error' | 'onClear'
>;

interface DatePickerSpecificProps extends Omit<DatePickerProps<Moment>, 'renderInput'> {
	includeTime?: false;
}

interface DateTimePickerSpecificProps extends Omit<DateTimePickerProps<Moment>, 'renderInput'> {
	includeTime: true;
}

export type ExtendedDatePickerProps = BaseExtendedPickerProps & (DatePickerSpecificProps | DateTimePickerSpecificProps);

/**
 * An extension of Mui's `DatePicker` or `DateTimePicker` component that utilizes our extended `TextField` component, and its simple validation.
 */
export const DatePicker = forwardRef<HTMLDivElement, ExtendedDatePickerProps & { hideBorder?: boolean }>(
	(
		{
			open: pOpen,
			onClose: pOnClose,
			onOpen: pOnOpen,
			label,
			status,
			helperText,
			size,
			fullWidth,
			onBlur,
			onClear,
			includeTime = false,
			hideBorder = false,
			...rest
		},
		ref
	) => {
		const { contextValue, isOpen, onOpen, onClose } = useMuiClickToDismiss(pOpen, pOnClose, pOnOpen);

		const PickerComponent = includeTime ? MuiDateTimePicker : MuiDatePicker;
		const format = includeTime ? 'DD/MM/YYYY hh:mm A' : 'DD/MM/YYYY';

		//TODO: Future i18n work
		return (
			<LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
				<ClickToDismissPortalContext.Provider value={contextValue}>
					<PickerComponent
						ref={ref}
						format={format}
						renderLoading={() => <CircularProgress />}
						slots={{
							textField: TextField as any
						}}
						slotProps={{
							textField: {
								status,
								helperText,
								label,
								size,
								fullWidth,
								onBlur,
								endAdornment: onClear ? <IconButton iconName="Clear" title="Clear" onClick={onClear} /> : <></>,
								...(hideBorder && {
									sx: {
										'& .MuiOutlinedInput-notchedOutline': {
											border: 'none'
										}
									}
								})
							}
						}}
						// To Do: Fix typing for dynamic picker component
						{...(rest as any)}
						onOpen={onOpen}
						onClose={onClose}
						open={isOpen}
					/>
				</ClickToDismissPortalContext.Provider>
			</LocalizationProvider>
		);
	}
);
